/* eslint-disable @typescript-eslint/tslint/config */
import { Injectable } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import { AppInsightsService, FeatureFlagService, genTinyGUID, GoogleTagManagerService } from '@cudirect/shared-ui-library';
import { LenderService } from '@shared/service/lender/lender.service';
import { EcpWorkflowEnvironmentService } from '@workflow/ecp-shared/service/environment/environment.service';
import { ValueLabelItem } from '@workflow/class/enum-item.class';
import { StatefulValue } from '@workflow/class/stateful-value.class';
import { LogicFnRef, PrimitiveType } from '@workflow/class/workflow.type';
import { maskValue } from '@workflow/directive/input-mask/mask.helper';
import { LogicService, LogicFnContext } from '@workflow/service/logic/logic.service';
import { UIFieldMapping } from '../../component/building-block/dynamic-personal-info/dynamic-personal-info.const';
import { DisclosureCategory } from '../../constant/disclosure-category.const';
import { PROTOCOL_MORTGAGE } from '../../product-helper';
import { Disclosure, DisclosureApplicant } from '../../types/disclosure.type';
import { JointCreditManager } from '../../types/joint-applicant/joint-credit-manager.class';
import { PersonalDynamic } from '../../types/personal-dynamic.type';
import { L360Service } from '../l360/l360.service';
import { getIterationId, getPageIterationId, processPath, validatePageOnPrefill } from '../utilities.service';
import { CustomUserInfo } from '../../types/custom-userinfo.type';
import { StatefulService } from '@app/service/stateful.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { ApplicationApiService } from '../application-api/application-api.service';
import { StateManagementService } from '@workflow/service/state-management/state-management.service';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { FlowService } from '@workflow/class/flow.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { getStateInfo } from 'src/postapp/consts/region-states.const';

const DOB_MONTHDATE = /^[0-9]{2}\/[0-9]{2}$/;
const buildEnumItem = (value, label?) => {
  return {
    label: label || value,
    value
  } as ValueLabelItem;
};

/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({
  providedIn: 'root'
})
export class CustomFunctionsService {
  constructor(
    private readonly logicService: LogicService,
    public readonly l360ApiService: L360Service,
    private readonly location: PlatformLocation,
    private readonly applicationInsightsService: AppInsightsService,
    private readonly lenderService: LenderService,
    private readonly gtmService: GoogleTagManagerService,
    private readonly environmentService: EcpWorkflowEnvironmentService,
    public readonly featureFlagService: FeatureFlagService,
    public readonly statefulService: StatefulService,
    private readonly stateManagementService: StateManagementService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    public readonly applicationApiService: ApplicationApiService,
    private readonly fieldService: FieldService,
    private readonly flowService: FlowService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {}

  /**
   * Build custom enum items for mortgage liability
   */
  public branchUsersEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const branchUsersStateful = this.statefulService.wrap('mem:branch-users');
    if (branchUsersStateful.value) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const branchUsers = <[]>JSON.parse(<string>branchUsersStateful.value);
      const buildItem = (branchUser) => {
        const label = context.customEnumConfig.labelTemplate
          .replace('{firstName}', branchUser.firstName)
          .replace('{lastName}', branchUser.lastName);

        const value = context.customEnumConfig.valuePath.replace('{userId}', branchUser.userId);

        return { label, value } as ValueLabelItem;
      };
      return branchUsers.map(buildItem);
    }

    return [];
  };

  public loanOfficerEnumBuilder = (context: LogicFnContext): ValueLabelItem[] => {
    const branchUsersStateful = this.statefulService.wrap('mem:loan-officer');
    if (branchUsersStateful.value) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const branchUsers = <[]>JSON.parse(<string>branchUsersStateful.value);
      const buildItem = (branchUser) => {
        const label = context.customEnumConfig.labelTemplate
          .replace('{firstName}', branchUser.firstName)
          .replace('{lastName}', branchUser.lastName);

        const value = context.customEnumConfig.valuePath.replace('{userId}', branchUser.userId);

        return { label, value } as ValueLabelItem;
      };
      return branchUsers.map(buildItem);
    }

    return [];
  };

  public requestAmtDescText = (context: LogicFnContext): string => {
    if (!context.field.config.payload) context.field.config.payload = {};
    if (!context?.field?.payload?.descText) {
      const minAmt = context.field.payload.min && maskValue('currency', context.field.payload.min.toString());
      const maxAmt = context.field.payload.max && maskValue('currency', context.field.payload.max.toString());
      context.field.config.payload.descText = minAmt && maxAmt && `Enter an amount between ${minAmt} and ${maxAmt}`;
    }
    return context?.field?.payload?.descText.toString();
  };

  /**
   * update instaTouch prefill data for oao flow
   */
  public updateDataForOao = (context: LogicFnContext, path: string): void => {
    /* Getting stringified Instatouch response from field context and parsing it to object */
    const data = context.field.data.valueStringOnly;
    const parsedData = data && JSON.parse(data);
    const pageIterationId = context.field.pageIterationId;
    if (parsedData) {
      if (parsedData.isManual) {
        const autoFillOption = parsedData?.isManual ? 'Manual' : 'MobileNumber';
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillOption`, undefined, pageIterationId).value = autoFillOption;
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/isPrefilled`, undefined, pageIterationId).value = parsedData?.isManual
          ? false
          : true;
        return;
      }

      // const timeout = 0;
      if (parsedData?.personalInfo) {
        // this.checkPreFilledFields(data.personalInfo);
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/lastName`).value = parsedData.personalInfo.identity.name.lastName;
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/homePhone`).value = parsedData.personalInfo.identity.contact.homePhone;
        this.checkSSN(parsedData, path);
        this.checkDOB(parsedData, path);
        this.checkAddress(parsedData, path);
        this.checkEmail(parsedData, path);
        this.checkEmployment(parsedData, path);
        this.checkAlertMessage(parsedData, path);
        this.checkFirstName(parsedData, path);
        const fieldData = this.fieldService.createField('oao-email', context.field.flowId).data;
        const isRegisteredApp = this.statefulService.wrap(`api:isRegisteredApplication`).value;
        if (!fieldData || !fieldData.value || !isRegisteredApp) {
          this.statefulService.wrap('ss:sso-flow-incorrectemail').value = true;
        }
      }

      // eslint-disable-next-line eqeqeq
    }
  };

  /**
   * Asynchronously checks for the presence of storageIdToValidate in stateful storage (ex. applicationId and applicantId),
   * polling until all storageIdToValidate are found, or the maximum retry count is reached. Rejects the promise if conditions
   * are not met within the allowed attempts.
   *
   * @returns A promise that resolves to `true` if both IDs are found, otherwise rejects.
   * @remarks This method should be revisited for potential improvements in polling logic.
   */
  public checkForAppId = (context: LogicFnContext, storageIdToValidate: string[]): Promise<boolean> => {
    return this.handlePartialSaveCheck(context, storageIdToValidate);
  };

  /**
   * Asynchronously checks for the presence of storageIdToValidate in stateful storage (ex. applicationId and applicantId),
   * polling until all storageIdToValidate are found, or the maximum retry count is reached. Rejects the promise if conditions
   * are not met within the allowed attempts.
   *
   * @returns A promise that resolves to `true` if both IDs are found, otherwise rejects.
   * @remarks This method should be revisited for potential improvements in polling logic.
   */
  public checkOLAForAppId = (context: LogicFnContext, storageIdToValidate: string[]): Promise<boolean> => {
    if (this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()) {
      return Promise.resolve(true);
    } else {
      return this.handlePartialSaveCheck(context, storageIdToValidate);
    }
  };

  /**
   * Method to handle the Partial Save Check for App Id's
   * @param context - current context
   * @param storageIdToValidate - array of ids
   * @returns A promise that resolves to `true` if both IDs are found, otherwise rejects.
   */
  public handlePartialSaveCheck(context: LogicFnContext, storageIdToValidate: string[]): Promise<boolean> {
    if (!storageIdToValidate || storageIdToValidate.length === 0) {
      const errMsg = `Cannot checkForAppId poll - workflow-page "${context.page.id}" is missing a storageIdToValidate.`;
      return Promise.reject(new Error(errMsg.toUpperCase()));
    }

    /** Retry count */
    let count = 0;

    /** Max number of polling retries - Retrieve from the environment service or use default */
    let maxRetryCount = this.environmentService?.ecpWorkflowEnvironment?.checkForAppIdAttempts;
    maxRetryCount = maxRetryCount > 0 ? maxRetryCount : 10; // We'll scale this back once we're stable / envir is wired up

    /** Identifiers for application and applicant data in storage */
    const storages = storageIdToValidate.map((id) => this.statefulService.wrap(id));

    return new Promise((resolve, reject) => {
      /** Delay between retries in milliseconds */
      const POLLING_INTERVAL = 5000;

      // Function to check if all storageIds have valid application/applicant IDs - called until conditions are met
      const check = () => {
        try {
          const isOLA = storageIdToValidate[0].substring(0, 3) === 'ola' ? true : false;
          // Do all storageIds passed to the method pass appIdsValid (ex. applicant/application id is valid)
          if (storages.every((a) => this.appIdsValid(a))) {
            // Triggers GA event once appId available
            this.emitAppIdCreatedEventToGA(storages[0].valueString, isOLA);
            resolve(true);
            return;
          } else if (count < maxRetryCount) {
            // Increment retry count before each attempt
            ++count;
            // Else, continue polling if max retry count not reached
            console.warn(`Retrying checkForAppId - attempt ${count}...`);
            setTimeout(() => {
              // Reload first storageId from storage and re-check using the method
              this.statefulService.wrap(storageIdToValidate[0]).load(check);
            }, POLLING_INTERVAL); // Delay between retries
          } else {
            // Reject the promise if retries exceed maximum allowed count
            const userInfo = this.buildCustomUserInfoData();
            const logName = isOLA ? 'ecp.ui.olaCheckForAppId' : 'ecp.ui.oaoCheckForAppId';
            this.applicationInsightsService.logEvent(logName, {
              info: userInfo,
              message: 'AppId retries exceeded maximum allowed count'
            });
            this.workflowLoader.end();
            reject(new Error('Max retry attempts reached on checkForAppId'));
          }
        } catch (error) {
          this.workflowLoader.hide();
          console.error('Error occurred during checkForAppId:', error);
          reject(error);
        }
      };
      // Method calls check() until conditions are met or max retry count is reached
      check();
    });
  }

  /**
   * Emits GA event once appId available
   */
  private emitAppIdCreatedEventToGA(applicationId: string, isOla = false): void {
    if (!this.statefulService.wrap('ss:trigger-appId-creation-event').value) return;
    const applicationType = isOla ? 'OLA' : 'OAO';
    const typestorage = isOla ? 'ola:olaApplicationInformation/loanGroupName' : 'api:oaoMemberInformation/membership/membershipName';
    const subTypeStorage = isOla ? 'ola:olaApplicationInformation/loanProductName' : '';
    const type = typestorage && this.statefulService.wrap(typestorage).valueStringOnly;
    const subType = subTypeStorage && this.statefulService.wrap(subTypeStorage).valueStringOnly;
    const entityId = this.statefulService.wrap('api:entityId').value;

    this.gtmService.publishEvent('Application.IDCreated', {
      ApplicationId: applicationId,
      ApplicationType: applicationType,
      ProductType: type,
      ProductSubType: subType,
      ArcDXApplicationID: entityId
    });
    let actionString = 'arcdx.';
    if (isOla) {
      actionString += 'loans.application-id-created';
    } else {
      actionString += 'accounts.application-id-created';
    }
    this.gtmService.publishEvent(actionString, {
      ApplicationId: applicationId,
      ApplicationType: applicationType,
      ProductType: type,
      ProductSubType: subType,
      ArcDXApplicationID: entityId
    });
    this.statefulService.wrap('ss:trigger-appId-creation-event').value = false;
  }

  /**
   * Method to build/construct User Info data needed for appinsights
   * @returns userInfo of type:CustomUserInfo
   */
  public buildCustomUserInfoData(): CustomUserInfo {
    const userFirstName = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').valueString;
    const userLastName = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/lastName').valueString;
    const userEmail = this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString;
    const applicationId = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    const primaryApplicantApplicantId = this.statefulService.wrap(
      'api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId'
    ).valueString;

    const userInfo = {
      applicantId: primaryApplicantApplicantId,
      applicationId,
      email: userEmail,
      lastName: userLastName,
      firstName: userFirstName
    } as CustomUserInfo;

    return userInfo;
  }
  /**
   * Checks if the provided `appId` is valid. A valid `appId` is considered to have a truthy `value`
   * and a `valueNumberOnly` greater than zero.
   *
   * @param appId - The `StatefulValue` instance representing the application/applicant ID to be validated.
   * @returns `true` if the application ID is valid, otherwise `false`.
   */
  private appIdsValid = (appId: StatefulValue) => {
    return appId.value && appId.valueNumberOnly > 0;
  };

  /**
   * update instaTouch prefill data for oao flow For FirstName.
   */
  public checkFirstName(parsedData, path) {
    if (parsedData.personalInfo.identity && parsedData.personalInfo.identity.name && parsedData.personalInfo.identity.name.firstName) {
      const names = this.separateNames(parsedData.personalInfo.identity.name.firstName);
      if (names.length > 0) {
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/firstName`).value = names[0];
      }
      if (names.length > 1) {
        this.statefulService.wrap(`api:oaoMemberInformation/${path}/middleName`).value = names[1];
      }
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillFirstName`).value = false;
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillFirstName`).value = true;
    }
  }
  private separateNames = (firstAndMiddleName: string) => {
    if (firstAndMiddleName) {
      const names = firstAndMiddleName.trim();
      const lastSpaceIndex = names.lastIndexOf(' ');
      if (lastSpaceIndex > 0) {
        return [names.substring(0, lastSpaceIndex), names.substring(lastSpaceIndex + 1)];
      }
    }
    return [firstAndMiddleName];
  };
  /**
   * update instaTouch prefill data for oao flow For Primary and co-applicant alert message.
   */
  public checkAlertMessage(parsedData, path) {
    // eslint-disable-next-line eqeqeq
    if (path == 'primaryApplicant') {
      if (parsedData && (parsedData.personalInfo || parsedData.employmentInfo)) {
        this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = true;
        this.statefulService.wrap('mem:primary-application-status').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillStatus').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/isPrefilled').value = true;
      } else {
        this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = false;
        this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillStatus').value = false;
      }
      this.statefulService.wrap('mem:previous-autofill-option').value = 'mobilenumber';
      // adding value in API
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/autoFillOption').value = 'MobileNumber';
      // eslint-disable-next-line eqeqeq
    } else if (path == 'coApplicant') {
      if (parsedData && (parsedData.personalInfo || parsedData.employmentInfo)) {
        this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = true;
        this.statefulService.wrap('mem:primary-application-status-co-applicant').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillStatus').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/coApplicant/isPrefilled').value = true;
      } else {
        this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = false;
        this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillStatus').value = false;
      }
      this.statefulService.wrap('mem:previous-autofill-option').value = 'mobilenumber';
      // adding value in API

      this.statefulService.wrap('api:oaoMemberInformation/coApplicant/autoFillOption').value = 'MobileNumber';
      // eslint-disable-next-line eqeqeq
    } else if (path == 'additionalCoApplicant') {
      if (parsedData && (parsedData.personalInfo || parsedData.employmentInfo)) {
        this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = true;
        this.statefulService.wrap('mem:primary-application-status-additional-co-app').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/iSPrefilledByMobile').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillStatus').value = true;
        this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/isPrefilled').value = true;
      } else {
        this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = false;
        this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillStatus').value = false;
      }
      this.statefulService.wrap('mem:previous-autofill-option').value = 'mobilenumber';
      // adding value in API
      this.statefulService.wrap('api:oaoMemberInformation/additionalCoApplicant/autoFillOption').value = 'MobileNumber';
    }
  }
  /**
   * update instaTouch prefill data for oao flow For Email.
   */
  public checkEmail(parsedData, path) {
    if (parsedData.personalInfo.identity.contact.email != null) {
      switch (path) {
        case 'primaryApplicant':
        case 'additionalCoApplicant':
        case 'coApplicant':
          this.statefulService.wrap(`api:oaoMemberInformation/${path}/email`).value = parsedData.personalInfo.identity.contact.email;
          break;
        default:
          break;
      }
    }
  }

  /**
   * update instaTouch prefill data for oao flow For Address.
   */
  public checkAddress(parsedData, path) {
    const timeout = 0;
    const addresses = parsedData.personalInfo.identity.addresses;
    if (addresses && addresses.length > 0) {
      StatefulValue.arrayTruncate(`api:oaoMemberInformation/${path}/primaryAddress`, () => {
        addresses.forEach((element, index) => {
          StatefulValue.arrayAdd(`api:oaoMemberInformation/${path}/primaryAddress`, () => {
            setTimeout(() => {
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/addressLine1`, index).value =
                element.streetAddress;
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/city`, index).value = element.city;
              if (element.state.length !== 2) {
                this.normalizeState(element);
              }
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/state`, index).value = element.state;
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/zipCode`, index).value = element.zipCode;
            }, timeout);
          });
        });
      });
    }
  }

  public normalizeState(address): void {
    const oldState = address.state;
    address.state = getStateInfo(address.state) || address.state;
    if (address.state !== oldState) {
      console.log('DataCleansing: State value updated from', oldState, 'to', address.state);
    }
  }

  /**
   * update instaTouch prefill data for oao flow For Employement.
   */
  public checkEmployment(parsedData, path) {
    const timeout = 0;
    if (parsedData && parsedData.employmentInfo) {
      const employers = parsedData.employmentInfo.employers;
      if (employers && employers.length > 0) {
        StatefulValue.arrayTruncate(`api:oaoMemberInformation/${path}/employments`, () => {
          employers.forEach((employerElement, index) => {
            StatefulValue.arrayAdd(`api:oaoMemberInformation/${path}/employments`, () => {
              setTimeout(() => {
                this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/employerName`, index).value =
                  employerElement.employerName;
                this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/jobTitle`, index).value =
                  employerElement.jobTitle;
                if (employerElement.employmentStatus && employerElement.employmentStatus.toLowerCase() === 'active') {
                  this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/employmentStatus`, index).value = 'Employed';
                }
                this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/startDate`, index).value =
                  employerElement.startDate;
                this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/income`, index).value =
                  employerElement.pay.annualIncome;
                this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/frequency`, index).value =
                  employerElement.pay.payFrequency;
              }, timeout);
            });
          });
        });
      }
    }
  }
  /**
   * update instaTouch prefill data for oao flow For SSN.
   */
  public checkSSN(parsedData, path) {
    if (parsedData.personalInfo.identification?.ssn?.length === 9) {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/ssn`).value = parsedData.personalInfo.identification.ssn;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillSSN`).value = false;
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillSSN`).value = true;
    }

    if (parsedData.personalInfo.identity?.contact?.mobile) {
      const mobileNumber = parsedData.givenMobileNumber ? parsedData.givenMobileNumber : parsedData.personalInfo.identity.contact.mobile;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/mobileNumber`).value = mobileNumber;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillMobileNumber`).value = false;
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/enableAutoFillMobileNumber`).value = true;
    }
  }
  /**
   * update instaTouch prefill data for oao flow For DOB.
   */
  public checkDOB(parsedData, path) {
    if (parsedData.personalInfo.identification && parsedData.personalInfo.identification.dob != null) {
      switch (path) {
        case 'coApplicant':
          this.statefulService.wrap('mem:primary-application-co-applicant-dob-valid').value = this.checkDOBValid(
            parsedData.personalInfo.identification.dob
          );
          break;
        case 'additionalCoApplicant':
          this.statefulService.wrap('mem:primary-application-additional-co-applicant-dob-valid').value = this.checkDOBValid(
            parsedData.personalInfo.identification.dob
          );
          break;
        case 'primaryApplicant':
        default:
          this.statefulService.wrap('mem:primary-application-dob-valid').value = this.checkDOBValid(
            parsedData.personalInfo.identification.dob
          );
          break;
      }
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/dateOfBirth`).value = parsedData.personalInfo.identification.dob;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillDateOfBirthValid`).value = true;
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillDateOfBirthValid`).value = false;
    }
  }
  /**
   * update instaTouch prefill data for oao business flow
   */
  public updateDataForOaoBusinessApplication = (context: LogicFnContext, type: string): void => {
    /* Getting stringified Instatouch response from field context and parsing it to object */
    const data = context?.field?.data?.valueStringOnly;
    const index = context?.field?.pageIterationId;
    const emp = 'employments';
    const address = 'primaryAddress';
    const authSigners = 'additionalAuthorizedSigners';
    const apiroot = `api:oaoMemberInformation/businessInformation/${type}/${index}`;
    const ssroot = `ss:oaoMemberInformation/businessInformation/${type}/${index}`;
    const resParty = 'responsibleParties';
    const parsedData = data && JSON.parse(data);
    if (parsedData) {
      const timeout = 0;
      if (parsedData.isManual) {
        const autoFillOption = parsedData?.isManual ? 'Manual' : 'MobileNumber';
        this.statefulService.wrap(`${apiroot}/autoFillOption`, undefined, index).value = autoFillOption;
        return;
      }
      if (parsedData && parsedData.personalInfo) {
        this.statefulService.wrap(`${apiroot}/isPrefilled`, undefined, index).value = parsedData?.isManual ? false : true;
        this.checkBusinessApplicantName(parsedData, apiroot);
        this.checkBusinessPhone(apiroot, parsedData);
        this.checkBusinessApplicantSSN(parsedData, apiroot, index, type, resParty);
        this.checkBusinessApplicantDOB(parsedData, apiroot);
        const addresses = parsedData.personalInfo.identity.addresses;
        this.checkAddressForbusiness(addresses, apiroot, address, timeout);
        const fieldData = this.fieldService.createField(
          'oao-business-responsible-person-email-#',
          context.field.flowId,
          undefined,
          index
        ).data;
        const isRegisteredApp = this.statefulService.wrap(`api:isRegisteredApplication`).value;
        if (!fieldData || !fieldData.value || !isRegisteredApp) {
          this.statefulService.wrap('ss:sso-flow-incorrectemail').value = true;
        }
      }

      /* Condition to ignore employment details for auth signers */
      if (type !== authSigners && parsedData?.employmentInfo) {
        this.checkEmploymentHistory(parsedData, apiroot, emp, timeout);
      }
      this.updateAutoFillStatus(apiroot, parsedData, ssroot);
    }
  };

  public checkDOBValid = (dobValue: string): boolean => {
    return dobValue && dobValue.match(DOB_MONTHDATE) ? false : true;
  };

  public replaceJointedPrimaryName = (context: LogicFnContext): string => {
    const parentBorrower = new JointCreditManager(PROTOCOL_MORTGAGE).getMainApplicantByChildIndex(context.field.pageIterationId);
    return parentBorrower && context.field.iterationId === 0
      ? context.field.config.tooltip.replace('#{primaryJointed}', parentBorrower.firstName)
      : '';
  };
  public or = (context: LogicFnContext, ...conditions: LogicFnRef[]): boolean => {
    const iterationId = getIterationId(context);
    const pageIterationId = getPageIterationId(context);
    return this.logicService.evaluate(conditions, context, 'collect', iterationId, pageIterationId).length !== conditions.length;
  };

  /** getDynamicEligibilityQuestions */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getDynamicEligibilityQuestions = (context: LogicFnContext): void => {
    if (this.l360ApiService.cachedEligibility !== null && this.l360ApiService.cachedEligibility !== undefined) {
      return;
    }
    const dynamicEligibilityEventId = genTinyGUID(24);
    this.l360ApiService.getDynamicEligibilityQuestions().subscribe((response) => {
      if (response.isSuccess && response.data !== undefined) {
        this.l360ApiService.cachedEligibility = response.data;
        this.logEvent('ecp.ui.dynamicEligibilityQuestions', 'end', dynamicEligibilityEventId, 'success');
      } else {
        this.logEvent('ecp.ui.dynamicEligibilityQuestions', 'end', dynamicEligibilityEventId, 'error');
      }
    });
  };

  private updateAutoFillStatus(apiroot: string, parsedData, ssroot: string) {
    this.statefulService.wrap(`${apiroot}/autoFillStatus`).value = true;
    if (parsedData && (parsedData.personalInfo || parsedData.employmentInfo)) {
      this.statefulService.wrap(`${ssroot}/autoFillAlert`).value = 'success';
    } else {
      this.statefulService.wrap(`${ssroot}/autoFillAlert`).value = 'warning';
    }
  }

  private checkBusinessApplicantDOB(parsedData, apiroot: string) {
    if (parsedData.personalInfo.identification && parsedData.personalInfo.identification.dob != null) {
      this.statefulService.wrap('mem:primary-application-dob-valid').value = this.checkDOBValid(parsedData.personalInfo.identification.dob);
      this.statefulService.wrap(`${apiroot}/dateOfBirth`).value = parsedData.personalInfo.identification.dob;
      this.statefulService.wrap(`${apiroot}/autoFillDateOfBirthValid`).value = true;
    } else {
      this.statefulService.wrap(`${apiroot}/autoFillDateOfBirthValid`).value = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private checkBusinessApplicantSSN(parsedData, apiroot: string, index: number, type: string, resParty: string) {
    if (parsedData.personalInfo.identification && parsedData.personalInfo.identification.ssn) {
      this.statefulService.wrap(`${apiroot}/ssn`).value = parsedData.personalInfo.identification.ssn;
      this.statefulService.wrap(`${apiroot}/enableAutoFillSSN`).value = false;
    } else {
      this.statefulService.wrap(`${apiroot}/enableAutoFillSSN`).value = true;
    }
    this.statefulService.wrap(`${apiroot}/email`).value = parsedData.personalInfo.identity.contact.email;
  }

  private checkBusinessPhone(apiroot: string, parsedData) {
    this.statefulService.wrap(`${apiroot}/homePhone`).value = parsedData.personalInfo.identity.contact.homePhone;
    if (parsedData?.personalInfo?.identity?.contact?.mobile) {
      const mobileNumber = parsedData.givenMobileNumber ? parsedData.givenMobileNumber : parsedData.personalInfo.identity.contact.mobile;
      this.statefulService.wrap(`${apiroot}/mobileNumber`).value = mobileNumber;
      this.statefulService.wrap(`${apiroot}/enableAutoFillMobileNumber`).value = false;
    } else {
      this.statefulService.wrap(`${apiroot}/enableAutoFillMobileNumber`).value = true;
    }
  }

  private checkBusinessApplicantName(parsedData, apiroot: string) {
    if (parsedData.personalInfo.identity && parsedData.personalInfo.identity.name && parsedData.personalInfo.identity.name.firstName) {
      const names = this.separateNames(parsedData.personalInfo.identity.name.firstName);
      if (names.length > 0) {
        this.statefulService.wrap(`${apiroot}/firstName`).value = names[0];
      }
      if (names.length > 1) {
        this.statefulService.wrap(`${apiroot}/middleName`).value = names[1];
      }
      this.statefulService.wrap(`${apiroot}/enableAutoFillFirstName`).value = false;
    } else {
      this.statefulService.wrap(`${apiroot}/enableAutoFillFirstName`).value = true;
    }

    this.statefulService.wrap(`${apiroot}/lastName`).value = parsedData.personalInfo.identity.name.lastName;
  }

  private checkAddressForbusiness(addresses, apiroot: string, address: string, timeout: number) {
    if (addresses && addresses.length > 0) {
      StatefulValue.arrayTruncate(`${apiroot}/${address}`, () => {
        addresses.forEach((element, addressindex) => {
          StatefulValue.arrayAdd(`${apiroot}/${address}`, () => {
            setTimeout(() => {
              this.statefulService.wrap(`${apiroot}/${address}/$/addressLine1`, addressindex).value = element.streetAddress;
              this.statefulService.wrap(`${apiroot}/${address}/$/city`, addressindex).value = element.city;
              this.statefulService.wrap(`${apiroot}/${address}/$/state`, addressindex).value = element.state;
              this.statefulService.wrap(`${apiroot}/${address}/$/zipCode`, addressindex).value = element.zipCode;
            }, timeout);
          });
        });
      });
    }
  }

  private checkEmploymentHistory(parsedData, apiroot: string, emp: string, timeout: number) {
    const employers = parsedData.employmentInfo.employers;
    if (employers && employers.length > 0) {
      StatefulValue.arrayTruncate(`${apiroot}/${emp}`, () => {
        employers.forEach((employerElement, empIndex) => {
          StatefulValue.arrayAdd(`${apiroot}/${emp}`, () => {
            setTimeout(() => {
              this.statefulService.wrap(`${apiroot}/${emp}/$/employerName`, empIndex).value = employerElement.employerName;
              this.statefulService.wrap(`${apiroot}/${emp}/$/jobTitle`, empIndex).value = employerElement.jobTitle;
              if (employerElement.employmentStatus && employerElement.employmentStatus.toLowerCase() === `active`) {
                this.statefulService.wrap(`${apiroot}/${emp}/$/employmentStatus`, empIndex).value = `Employed`;
              }
              this.statefulService.wrap(`${apiroot}/${emp}/$/startDate`, empIndex).value = employerElement.startDate;
              this.statefulService.wrap(`${apiroot}/${emp}/$/income`, empIndex).value = employerElement.pay.annualIncome;
              this.statefulService.wrap(`${apiroot}/${emp}/$/frequency`, empIndex).value = employerElement.pay.payFrequency;
            }, timeout);
          });
        });
      });
    }
  }

  /** logEvent */
  private logEvent(eventName: string, eventState: 'start' | 'end', correlationId: string, status?: 'success' | 'error'): void {
    this.applicationInsightsService.logEvent(eventName, {
      eventState,
      correlationId,
      status
    });
  }

  /**
   * When there is a fragment in url it is treated as navigated from Application review or Checkpoint
   * @param context
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isNotRoutedFromReviewPage = (context: LogicFnContext): boolean => {
    return !this.location.hash?.length;
  };

  /**
   * Method to check for any invalid fields
   * if any requireds fileds invalid it will force to ediit mode in prefill
   */
  public validatePageOnPrefill = (context: LogicFnContext, previewCardId): void => {
    const isStreamlined = this.flowService.createFlow(context.page?.flowId).config.streamlined;
    validatePageOnPrefill(context, this.location, previewCardId, isStreamlined);
  };

  /**
   * @param context workflow cardContext or pageContext
   * @returns true|false
   */
  public isNotStreamlinedFlow = (context: LogicFnContext): boolean => {
    const flowId = (context.page || context.card)?.flowId;
    return flowId && !this.flowService.createFlow(flowId).config.streamlined;
  };

  /**
   * Method to check the page has any data and set mem flag
   * Based on the flag we are showing the subtitle on pages
   * @param context Logic context
   */
  public checkPageHasData = (context: LogicFnContext): void => {
    const streamlined = this.flowService.createFlow(context.page.flowId).config.streamlined;
    if (
      streamlined ||
      context.page.cards.filter((t) => t.visible && t.helper.flatVisibleFields.findIndex((f) => f.required && f.valid) !== -1).length > 0
    ) {
      this.statefulService.wrap('mem:has-data-' + context.page.id, null, context.page?.pageIterationId).value = true;
    } else {
      this.statefulService.wrap('mem:has-data-' + context.page.id, null, context.page?.pageIterationId).value = false;
    }
  };

  /**
   * Load OAO Cross-Sell Disclosure Content
   * @param pageId
   */
  public loadCrossSellDisclosure = (_context: LogicFnContext): Promise<boolean> => {
    let crossSellDisclosure: Disclosure;
    // Handle Network/API Failure
    this.statefulService.wrap('mem:hasCrossSell').value = true;
    return new Promise<boolean>((resolve) => {
      const subscription = this.l360ApiService.disclosureSub.subscribe((response) => {
        if (response && response.length > 0) {
          const disclosures = response.filter((t) => t.category && t.category.toLowerCase() === DisclosureCategory.CorssSell.toLowerCase());
          if (disclosures?.length) {
            const lenderName = this.lenderService.lenderDataInfo?.lenderName;
            crossSellDisclosure = disclosures[0];
            crossSellDisclosure.content = crossSellDisclosure.content.replace(/{lenderName}/gi, lenderName);
            this.setApplicants(crossSellDisclosure);
            _context.field.config.content = JSON.stringify(crossSellDisclosure);
          }
          subscription.unsubscribe();
        }
        this.statefulService.wrap('mem:hasCrossSell').value = crossSellDisclosure ? false : true;
        resolve(true);
      });
      this.l360ApiService.getDisclsoures();
    });
  };

  /**
   * Load OAO Smart Sign Disclosure Content
   * @param pageId
   */
  public loadSmartSignDisclosure = (_context: LogicFnContext): Promise<boolean> => {
    let smartSignDisclosure: Disclosure[] = [];
    let applicants: DisclosureApplicant[] = [];
    // Handle Network/API Failure
    this.statefulService.wrap('mem:hasSmartSign').value = true;
    return new Promise<boolean>((resolve) => {
      this.l360ApiService.disclosureSub.subscribe((response) => {
        if (response && response.length > 0) {
          smartSignDisclosure = response.filter(
            (t) => t.category && t.category.toLowerCase() === DisclosureCategory.SmartSign.toLowerCase()
          );
          const lenderName = this.lenderService.lenderDataInfo?.lenderName;
          smartSignDisclosure.forEach((item, i) => {
            applicants = [];
            item.content = item.content.replace(/{lenderName}/gi, lenderName);
            item.storageId = `api:oaoMemberInformation/smartSignDisclosure/${i}/accepted`;
            item.agreedDateStorageId = `api:oaoMemberInformation/smartSignDisclosure/${i}/disclosureAgreeDate`;
            this.setSmartSignApplicants(item, item.isReadToAccept, applicants, i);
          });
        }
        this.statefulService.wrap('mem:hasSmartSign').value = smartSignDisclosure.length ? false : true;
        _context.field.config.content = smartSignDisclosure.length ? JSON.stringify(smartSignDisclosure) : '';
        resolve(true);
      });
      this.l360ApiService.getDisclsoures();
    });
  };

  /**
   * Set Applicants for Disclosures
   * @param item
   * @param checkAllowToAccept
   * @param applicants
   * @param i
   */
  private setSmartSignApplicants(item: Disclosure, checkAllowToAccept: boolean, applicants: DisclosureApplicant[], i: number): void {
    let allowToAccept = false;
    const protocol = 'api:';
    const storagePath = 'oaoMemberInformation/smartSignDisclosure';
    item.applicants.forEach((value, index) => {
      if (value.applicant) {
        const currentRequestBodyInSS = this.statefulService.wrap('ss:current-patch-request-body').valueString;
        const isAcceptBundleValue = this.statefulService.wrap(protocol + storagePath + `/${i}/applicants/${index}/isAccept`).value;
        const isFinalReviewSmartSignAgreed = this.statefulService.wrap(
          protocol + 'oaoMemberInformation/isFinalReviewSmartSignAgreed'
        ).value;
        const isAccept = this.determineIsAcceptValue(
          storagePath + `/${i}/applicants/${index}/isAccept`,
          currentRequestBodyInSS,
          isAcceptBundleValue,
          isFinalReviewSmartSignAgreed
        );
        if (isAccept && checkAllowToAccept) {
          allowToAccept = true;
        }
        applicants.push({
          accepted: isAccept,
          storageId: protocol + storagePath + `/${i}/applicants/${index}/isAccept`,
          agreedDateStorageId: protocol + storagePath + `/${i}/applicants/${index}/acceptDateTime`,
          label: `${
            item.placeHolderAcceptText
              ? item.placeHolderAcceptText.replace('$Applicant Name$', value.applicant)
              : `I, ${value.applicant}, authorize this credit check.`
          }`,
          applicant: `${value.applicant}`
        });
      }
    });
    item.applicants = applicants;
    item.allowToAccept = checkAllowToAccept ? allowToAccept : true;
  }

  /**
   * Method to determine the Accept value which is responsible for bindig the checkbox value downstream.
   * Compares the session value with the bundle value before returning out
   * @param storagePath - storage path of smartSignDisclosure
   * @param currentRequestBodyInSS - request body from SS in string format
   * @param isAcceptBundleValue - current bundle value
   * @param isFinalReviewAgreed - current isFinalReviewAgreed boolean value
   * @returns boolean value of true if value already exists or else false
   */
  public determineIsAcceptValue(
    storagePath: string,
    currentRequestBodyInSS: string,
    isAcceptBundleValue: PrimitiveType,
    isFinalReviewAgreed: PrimitiveType
  ): boolean {
    let isAccept = false;
    let currentRequestBodyInSSParsedValue = [];
    if (currentRequestBodyInSS && currentRequestBodyInSS.length > 0) {
      currentRequestBodyInSSParsedValue = JSON.parse(currentRequestBodyInSS);
    }
    const isAcceptSession = currentRequestBodyInSSParsedValue.filter((f) => f.path.toString() === storagePath);
    let isAcceptSessionValue = false;
    if (isAcceptSession && isAcceptSession.length > 0) {
      isAcceptSessionValue = isAcceptSession[isAcceptSession.length - 1].value;
    }
    isAccept = isFinalReviewAgreed ? (isAcceptBundleValue as boolean) : isAcceptSessionValue;
    return isAccept;
  }

  /**
   * Set Applicants for Disclosures
   * @param crossSellDisclosure
   * @param applicants
   */
  private setApplicants(crossSellDisclosure: Disclosure): void {
    let allowToAccept = false;
    const applicants: DisclosureApplicant[] = [];
    const protocol = 'api:';
    const storagePath = 'oaoMemberInformation/crossSell';
    crossSellDisclosure.applicants.forEach((value, index) => {
      if (value.applicant) {
        const currentRequestBodyInSS = this.statefulService.wrap('ss:current-patch-request-body').valueString;
        const isAcceptBundleValue = this.statefulService.wrap(protocol + storagePath + `/applicants/${index}/isAccept`).value;
        const isFinalReviewCrossSellAgreed = this.statefulService.wrap(
          protocol + 'oaoMemberInformation/isFinalReviewCrossSellAgreed'
        ).value;
        const isAccept = this.determineIsAcceptValue(
          storagePath + `/applicants/${index}/isAccept`,
          currentRequestBodyInSS,
          isAcceptBundleValue,
          isFinalReviewCrossSellAgreed
        );
        allowToAccept = isAccept ? true : allowToAccept;

        applicants.push({
          accepted: this.getAcceptedValue(isFinalReviewCrossSellAgreed, isAccept),
          storageId: protocol + storagePath + `/applicants/$/isAccept`,
          agreedDateStorageId: protocol + storagePath + `/applicants/$/acceptDateTime`,
          acceptTextStorageId: protocol + storagePath + `/applicants/$/acceptText`,
          label: `${
            crossSellDisclosure.placeHolderAcceptText
              ? crossSellDisclosure.placeHolderAcceptText.replace('$Applicant Name$', value.applicant)
              : `I, ${value.applicant}, authorize this credit check.`
          }`,
          applicant: `${value.applicant}`
        });
      }
    });
    crossSellDisclosure.applicants = applicants;
    crossSellDisclosure.allowToAccept = allowToAccept;
  }

  /**
   * Method to detrmine the Cross-Sell Applicants Disclosure Accepted Value
   * @param isFinalReviewCrossSellAgreed - boolean value for isFinalReviewCrossSellAgreed
   * @param isAccept - current isAccept value
   * @returns
   */
  private getAcceptedValue(isFinalReviewCrossSellAgreed: PrimitiveType, isAccept: boolean): boolean {
    if (isFinalReviewCrossSellAgreed) {
      return isAccept;
    }
    // Since the cross-sell disclosures have two options: (Opt-in -> true && Opt-out -> false)
    // making the value as null in case of false
    return !isAccept ? null : isAccept;
  }

  /**
   * Load Identification Types on page loan incase of Hb prefill
   * @param _context Login Func
   * @param fieldId string
   */
  public prefillIdentificationType = (_context: LogicFnContext, fieldId: string): void => {
    const field = this.fieldService.createField(fieldId, _context.page?.flowId, null, _context.page.pageIterationId);
    const membershipId = this.getMembershipId('prefillIdentificationType');
    if (!field.config.payload) {
      field.config.payload = {};
    }

    if (field.config.payload.membershipId !== membershipId) {
      field.config.payload = {};
      field.config.payload.membershipId = membershipId;
    }

    if (
      field.config.payload.isLoading ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (field.config.payload.dropdownLists && (field.config.payload.dropdownLists as any).length > 0)
    ) {
      this.onChangePrefillIdTypes(field);
    } else {
      field.config.payload.isLoading = true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.l360ApiService.getIdentificationTypes(membershipId.toString()).subscribe((response: any) => {
        if (response && response.data) {
          const dropdownValues = response.data;
          if (!dropdownValues) {
            return;
          }
          const fieldsTypeConfig = response.data;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const list = Array.from(new Set(dropdownValues)).map((item: any) => buildEnumItem(item.id, item.name));
          // eslint-disable-next-line max-len, prettier/prettier, @typescript-eslint/no-explicit-any
          const lstFieldsTypeConfig = Array.from(new Set(fieldsTypeConfig)).map((item: any) =>
            buildEnumItem(item.id, item.fieldsTypeConfig)
          );
          field.config.payload.dropdownLists = JSON.stringify(list);
          field.config.payload.fieldsTypeConfig = JSON.stringify(lstFieldsTypeConfig);
          field.config.payload.isLoading = false;
          this.onChangePrefillIdTypes(field);
          return list;
        }
      });
    }
  };

  /**
   * On Change event of Prefill Id Type dd
   * @param field Field
   */
  public onChangePrefillIdTypes = (field: FieldInstance): void => {
    try {
      const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
      let idType;
      if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
        idType = this.statefulService.wrap(field.config.storageId, null, field.pageIterationId).value;
      } else {
        idType = this.statefulService.wrap(field.config.storageId).value;
      }

      const objIdentificationChildFields: PersonalDynamic = JSON.parse(JSON.stringify(UIFieldMapping)) as PersonalDynamic;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-assertion
      if (field.config.payload.fieldsTypeConfig && (field.config.payload.fieldsTypeConfig as any)?.length > 0) {
        const lstFieldsTypeConfig = field.config.payload.fieldsTypeConfig
          ? (JSON.parse(field.config.payload.fieldsTypeConfig as string) as ValueLabelItem[])
          : [];

        this.setPrefillIdTypes(field, lstFieldsTypeConfig, idType, objIdentificationChildFields);
      }
    } catch (e) {}
  };

  // TODO: Why are these all any?
  /**
   * Set Id type values
   * @param field Field
   * @param lstFieldsTypeConfig Field Config
   * @param idType Id Type
   * @param objIdentChildFields Child fields
   * @returns void
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly setPrefillIdTypes = (
    field: FieldInstance,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lstFieldsTypeConfig: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    idType: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    objIdentChildFields: any
  ): void => {
    const membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    if (lstFieldsTypeConfig && lstFieldsTypeConfig.filter((t) => t.value === idType).length > 0) {
      const objSelectedTypeFields = lstFieldsTypeConfig.filter((t) => t.value === idType)[0].label as string;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fieldArrays = objSelectedTypeFields.split('|') as any[];
      fieldArrays.shift();

      if (!fieldArrays || fieldArrays.length === 0) {
        return;
      }

      fieldArrays.forEach((element) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const uiFields = element.split(':') as any[];
        if (uiFields[0] !== 'IssuedByType') {
          if (objIdentChildFields[uiFields[0]]) {
            objIdentChildFields[uiFields[0]].isRequired = /true/i.test((uiFields[1] as string).toLowerCase());
          }
        } else if (uiFields[0] === 'IssuedByType' && objIdentChildFields.IssuedBy.isRequired) {
          switch (uiFields[1]) {
            case 'FreeText':
              objIdentChildFields.IssuingInstitution.isRequired = true;
              break;
            case 'Country':
              objIdentChildFields.IssuingCountry.isRequired = true;
              break;
            case 'State':
              objIdentChildFields.IssuingState.isRequired = true;
          }
        }
      });
      const strChildFields = JSON.stringify(objIdentChildFields);
      this.setIdTypeConfig(membershipTypeName, field, strChildFields, false);
    } else {
      this.setIdTypeConfig(membershipTypeName, field, '', false);
    }
  };

  /**
   * Set Id type config value into bundle
   * @param membershipTypeName string
   * @param field Field
   * @param val string
   * @param setEmptyValue boolean
   */
  private setIdTypeConfig(membershipTypeName: string, field: FieldInstance, val: string, setEmptyValue: boolean): void {
    if (setEmptyValue === true) {
      if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
        this.statefulService.wrap(field.config.storageId, null, field.pageIterationId).value = '';
        this.statefulService.wrap('mem:edit-oao-business-res-personal-preview-#', null, field.pageIterationId).value = true;
      } else {
        this.statefulService.wrap(field.config.storageId).value = '';
      }
    } else {
      const storageId = field.config.storageId.replace('/idType', '/identificationFieldsTypeConfig');
      if (membershipTypeName && membershipTypeName.toLowerCase() === 'business') {
        this.statefulService.wrap(storageId, null, field.pageIterationId).value = val;
      } else {
        this.statefulService.wrap(storageId).value = val;
      }
    }
  }

  /* Get MembershipId */
  private getMembershipId(operationName: string): string {
    const memCorrelationId = genTinyGUID(24);
    this.logAppInsightsMembershipId(memCorrelationId, null, operationName, 'start');
    const bundleMembershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const dropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
    if (!bundleMembershipId && !dropdownMembershipId) {
      const memMembershipId = this.statefulService.wrap('mem:oaoMemberInformation/membership/membershipID').value;
      const memdropdownMembershipId = this.statefulService.wrap('ss:oaoMemberInformation/membership/dropdownMembershipID').value;
      const tempMembershipId = memMembershipId ? memMembershipId : memdropdownMembershipId;
      if (!tempMembershipId) {
        this.logAppInsightsMembershipId(memCorrelationId, 'null', operationName, 'end', 'error');
        return 'null';
      }
      return tempMembershipId.toString();
    }
    const membershipId = bundleMembershipId ? bundleMembershipId.toString() : dropdownMembershipId.toString();
    this.logAppInsightsMembershipId(memCorrelationId, membershipId, operationName, 'end', 'success');
    return membershipId;
  }

  /* Add logging */
  private logAppInsightsMembershipId(
    correlationId: string,
    membershipId: string,
    operationName: string,
    eventState: 'start' | 'end',
    status: 'success' | 'error' = null
  ): void {
    this.applicationInsightsService.logEvent('ecp.ui.getMembershipId', {
      membershipId,
      eventState,
      operationName,
      correlationId,
      status
    });
  }

  /**
   * Set value vy Condition
   * @param context
   * @param storageId
   * @param value
   * @param falseValue
   * @param conditions
   */
  public setValueByCond = (
    context: LogicFnContext,
    storageId: string,
    value: PrimitiveType,
    falseValue: PrimitiveType,
    ...conditions: LogicFnRef[]
  ): void => {
    const iterationId = getIterationId(context);
    const pageIterationId = getPageIterationId(context);
    const isValid = this.logicService.evaluate(conditions, context, true, iterationId, pageIterationId);

    if (isValid) {
      this.statefulService.wrap(storageId, iterationId, pageIterationId).value = value;
    } else {
      this.statefulService.wrap(storageId, iterationId, pageIterationId).value = falseValue;
    }
  };

  /**
   * Set Additional Consent Array
   * @param context Page Context
   * @param additonalConsentRootPath rootPath
   * @param setWhenSplash 'enabled'|'disabled'|'disabledForFirstParty'|'both'
   */
  public setAdditionalConsents = (context: LogicFnContext, additonalConsentRootPath: string, setWhenSplash: string): void => {
    // do not set additional in splashscreeen consent when splashscreen is disabled, this to avoid possible null values in consents
    if (
      setWhenSplash === 'enabled' &&
      !this.adminPortalFeaturesService.config.consentManagement.primaryAcknowledgement.enableConsentScreen
    ) {
      return;
    }
    // do not set additional in persnal info consent when splashscreen is enabled, this to avoid possible null values in consents
    if (
      setWhenSplash === 'disabled' &&
      this.adminPortalFeaturesService.config.consentManagement.primaryAcknowledgement.enableConsentScreen
    ) {
      return;
    }
    // do not set additional consent in responsible party screen when splashscreen is enabled, and page iteration id is 0,
    // this to avoid possible null values in consents for Business flow
    // Sinace we have specific flag "setWhenSplash" we don't need to check is busness flow.
    if (
      setWhenSplash === 'disabledForFirstParty' &&
      this.adminPortalFeaturesService.config.consentManagement.primaryAcknowledgement.enableConsentScreen &&
      context.pageIteration === 0
    ) {
      return;
    }

    type ConsentType = { acknowledgeId: string; isAccepted: boolean; acceptanceSnapshotReferenceId: string; agreedDate: string };
    const setDefaultBusinessPageIterationId = additonalConsentRootPath.includes('#') && context.page.pageIterationId === undefined;
    const pageIterationId = setDefaultBusinessPageIterationId ? 0 : context.page.pageIterationId;
    const processPathArgument = setDefaultBusinessPageIterationId ? 0 : context;
    const additionalConsents = this.adminPortalFeaturesService.config.consentManagement?.additionalAcknowledgements || [];
    const processedPath = processPath(additonalConsentRootPath, processPathArgument);
    const arraySize = StatefulValue.arraySize(additonalConsentRootPath, pageIterationId);
    const updatedConsents = this.stateManagementService.getValue<ConsentType[]>(additonalConsentRootPath) || [];

    const actaulAcknoledgeIds = additionalConsents.map((t) => t.acknowledgeId);
    const updatedAcknoledgeIds = updatedConsents.map((t) => t.acknowledgeId);

    // When additionaconsent exists and all consents accepted do nothing.
    if (updatedConsents.length > 0 && updatedConsents.length === updatedConsents.filter((u) => u.isAccepted).length) {
      return;
    }
    // Get any difference with additional consents and consents in the bundle.
    const difference = actaulAcknoledgeIds
      .filter((x) => !updatedAcknoledgeIds.includes(x))
      .concat(updatedAcknoledgeIds.filter((x) => !actaulAcknoledgeIds.includes(x)));

    // If additional consent in the bundle count mismatch with admin settings then clear and add additional cosents in the bundle
    // if not do nothing
    if (!difference.length) {
      return;
    }

    // if bundle has existing consents clear and recreate entries
    if (arraySize > 0) {
      StatefulValue.arrayTruncateWithPageIteration(processedPath, () => {
        setTimeout(() => {
          additionalConsents.forEach((consent, index) => {
            StatefulValue.arrayAdd(processedPath);
            const oldValue = updatedConsents.find((a) => a.acknowledgeId === consent.acknowledgeId);
            if (oldValue) {
              this.statefulService.wrap(`${processedPath}/${index}/acceptanceSnapshotReferenceId`).value =
                oldValue.acceptanceSnapshotReferenceId;
              this.statefulService.wrap(`${processedPath}/${index}/acceptanceText`).value = consent.title;
              this.statefulService.wrap(`${processedPath}/${index}/acknowledgeId`).value = oldValue.acknowledgeId;
              this.statefulService.wrap(`${processedPath}/${index}/agreedDate`).value = oldValue.agreedDate;
              this.statefulService.wrap(`${processedPath}/${index}/isAccepted`).value = oldValue.isAccepted;
            }
          });
        }, 0);
      });
    } else {
      additionalConsents.forEach(() => {
        StatefulValue.arrayAdd(processedPath, () => {});
      });
    }
  };
}
